import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { LoadingSpinner } from "@/core/components/loading-spinner";
import { Button } from "@/core/components/ui/button";
import { Stack } from "@/core/components/ui/stack/stack";
import { toast } from "@/core/components/ui/toast";
import { Typography } from "@/core/components/ui/typography";

export const OAuthCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isProcessing, setIsProcessing] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const oauthMessage = searchParams.get("oauth_message");
  const businessId = searchParams.get("business_id");
  const redirectTo = searchParams.get("redirect_to");

  useEffect(() => {
    if (oauthMessage) {
      if (oauthMessage.includes("Successfully linked")) {
        setSuccess(true);
        toast.success("Google Business account connected successfully!");

        const timeout = setTimeout(() => {
          if (redirectTo === "post-onboarding") {
            navigate("/waiting?google_business_modal=open", { replace: true });
          } else if (businessId) {
            navigate(`/business-settings/${businessId}`, { replace: true });
          } else {
            navigate("/dashboard", { replace: true });
          }
        }, 2000);

        return () => clearTimeout(timeout);
      } else {
        setError(oauthMessage);
        setIsProcessing(false);
      }
    } else {
      setError("No OAuth message received from server");
      setIsProcessing(false);
    }
  }, [oauthMessage, businessId, redirectTo, navigate]);

  if (isProcessing && !success) {
    return (
      <Stack fullWidth align="center" justify="center" className="h-full p-4">
        <Stack gap="md" align="center">
          <LoadingSpinner size="lg" />
          <Typography variant="subtitleW1" className="text-center">
            Connecting your Google Business account...
          </Typography>
          <Typography
            variant="bodyW1"
            className="text-center text-secondary-text"
          >
            Please wait while we complete the setup.
          </Typography>
        </Stack>
      </Stack>
    );
  }

  if (error) {
    return (
      <Stack fullWidth align="center" justify="center" className="h-full p-4">
        <Stack gap="md" align="center" className="max-w-md">
          <Typography variant="subtitleW1" className="text-center text-red-600">
            Connection Failed
          </Typography>
          <Typography
            variant="bodyW1"
            className="text-center text-secondary-text"
          >
            {error}
          </Typography>
          <Stack direction="row" gap="sm">
            <Button variant="secondary" onClick={() => navigate(-1)}>
              Go Back
            </Button>
            {redirectTo === "post-onboarding" ? (
              <Button
                onClick={() => navigate("/waiting?google_business_modal=open")}
              >
                Try Again
              </Button>
            ) : businessId ? (
              <Button
                onClick={() => navigate(`/business-settings/${businessId}`)}
              >
                View Business Settings
              </Button>
            ) : null}
          </Stack>
        </Stack>
      </Stack>
    );
  }

  if (success) {
    return (
      <Stack fullWidth align="center" justify="center" className="h-full p-4">
        <Stack gap="md" align="center">
          <Typography
            variant="subtitleW1"
            className="text-center text-green-600"
          >
            Success!
          </Typography>
          <Typography
            variant="bodyW1"
            className="text-center text-secondary-text"
          >
            Your Google Business account has been connected successfully.
          </Typography>
          <Typography
            variant="captionW1"
            className="text-center text-secondary-text"
          >
            Redirecting to business settings...
          </Typography>
        </Stack>
      </Stack>
    );
  }

  return null;
};
